'use client';

import { useState, useEffect } from 'react';
import { Copy, Check, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { cssMinifyContent } from '../../data/css-minify-content';
import Link from 'next/link';

export default function CssMinifier() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const savedInput = localStorage.getItem('cssMinifierInput');
        if (savedInput) {
            setInput(savedInput);
        }
    }, []);

    const minifyCss = () => {
        try {
            if (!input.trim()) {
                setError('Please enter some CSS to minify');
                return;
            }

            // Basic CSS minification logic
            let minified = input;

            // Remove comments
            minified = minified.replace(/\/\*[\s\S]*?\*\//g, '');

            // Remove whitespace
            minified = minified.replace(/\s+/g, '');

            // Remove space around brackets and colons
            minified = minified.replace(/\s*([{}:;,])\s*/g, '$1');

            // Remove last semicolon before closing bracket
            minified = minified.replace(/;}/g, '}');

            // Remove space before and after selectors
            minified = minified.replace(/\s*([>+~])\s*/g, '$1');

            // Remove space around { and }
            minified = minified.replace(/\s*\{\s*/g, '{');
            minified = minified.replace(/\s*\}\s*/g, '}');

            // Remove space around :
            minified = minified.replace(/\s*:\s*/g, ':');

            // Remove space around ,
            minified = minified.replace(/\s*,\s*/g, ',');

            // Remove space around !important
            minified = minified.replace(/\s*!important/g, '!important');

            setOutput(minified);
            setError('');

            localStorage.setItem('cssMinifierInput', input);
            toast.success('CSS minified successfully!');
        } catch (err) {
            setError('Error minifying CSS: ' + (err as Error).message);
            setOutput('');
        }
    };

    const copyToClipboard = () => {
        if (!output) return;

        navigator.clipboard.writeText(output).then(() => {
            setCopied(true);
            toast.success('Copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const clearAll = () => {
        setInput('');
        setOutput('');
        setError('');
        localStorage.removeItem('cssMinifierInput');
    };

    return (
        <div className="min-h-screen bg-[#F9FAFB] py-10 text-slate-900 dark:bg-slate-950 dark:text-slate-50">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                {/* Page Header */}
                <div className="mb-8 text-center">
                    <ToolPageIntro title="CSS Minifier">
                Minify CSS code to reduce file size and improve website performance. Our free CSS minifier removes whitespace, comments, and unnecessary characters.
            </ToolPageIntro>
                </div>

                {/* Main tool card – full width */}
                <div className="space-y-6">
                    {/* Tool Interface */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/80">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="text-left">
                                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                                        Minify your CSS
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Paste CSS on the left, get compressed, optimized output on the right.
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={clearAll}
                                        className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                    >
                                        Clear all
                                    </button>
                                    <button
                                        onClick={minifyCss}
                                        className="inline-flex items-center rounded-full bg-[#4F46E5] px-4 py-1.5 text-xs font-semibold text-white shadow-sm shadow-indigo-500/30 transition hover:bg-[#4338CA] hover:shadow-md"
                                    >
                                        Minify CSS
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 divide-x divide-slate-200 dark:divide-slate-800 lg:grid-cols-2">
                            {/* Input Section */}
                            <div className="p-4">
                                <div className="mb-3 flex items-center justify-between">
                                    <h3 className="text-sm font-medium text-slate-900 dark:text-slate-50">
                                        Input CSS
                                    </h3>
                                </div>
                                <div className="relative">
                                    <textarea
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        className="h-96 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono text-slate-900 placeholder-slate-400 shadow-sm transition-colors focus:border-[#4F46E5] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
                                        placeholder="Paste your CSS code here..."
                                        spellCheck={false}
                                    />
                                </div>
                            </div>

                            {/* Output Section */}
                            <div className="p-4 bg-slate-50/50 dark:bg-slate-900/50">
                                <div className="mb-3 flex items-center justify-between">
                                    <h3 className="text-sm font-medium text-slate-900 dark:text-slate-50">
                                        Minified CSS
                                    </h3>
                                    <button
                                        onClick={copyToClipboard}
                                        disabled={!output}
                                        className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${output
                                            ? 'border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                                            : 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500'
                                            }`}
                                    >
                                        {copied ? (
                                            <>
                                                <Check className="h-3.5 w-3.5" />
                                                Copied!
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="h-3.5 w-3.5" />
                                                Copy
                                            </>
                                        )}
                                    </button>
                                </div>
                                <div className="relative">
                                    <textarea
                                        value={output}
                                        readOnly
                                        className="h-96 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono text-slate-900 shadow-sm transition-colors dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                                        placeholder="Your minified CSS will appear here..."
                                    />
                                </div>
                                {error && (
                                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800/30 dark:bg-red-900/20">
                                        <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" />
                                        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Informational Content */}
                    <InformationalContent
                        title={cssMinifyContent.title}
                        description={cssMinifyContent.description}
                        content={cssMinifyContent.content}
                        faqs={cssMinifyContent.faqs}
                        relatedTools={cssMinifyContent.relatedTools}
                    />
                </div>
            </div>
        </div>
    );
}
